/**
 * ClientLogoUpload Component (Story 3.4)
 *
 * Upload area for client logos in EditClientSheet.
 * Supports drag-and-drop + click-to-browse.
 *
 * Three-step upload flow:
 * 1. POST /api/clients/:id/logo/upload-url → presigned URL
 * 2. PUT to R2 directly with file
 * 3. PATCH /api/clients/:id/logo → confirm with logoUrl + updatedAt
 *
 * Features:
 * - Client-side file validation (type + size)
 * - Preview via URL.createObjectURL with proper cleanup
 * - Disabled during active upload (prevents race conditions)
 * - Remove button for existing logos
 * - Accessible: keyboard activation, aria-labels, aria-live progress
 */

import { useState, useRef, useEffect } from "react";
import { Button, cn } from "@provenleap/ui";
import { apiPost, apiPatch } from "../../lib/apiClient";
import { LoadingSpinner } from "../icons";
import { ClientAvatar } from "./ClientAvatar";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

interface ClientLogoUploadProps {
  clientId: string;
  clientName: string;
  currentUrl: string | null;
  updatedAt: string;
  onUploadComplete: (newUrl: string, newUpdatedAt: string) => void;
  onRemoveComplete: (newUpdatedAt: string) => void;
  onError?: (message: string) => void;
}

export function ClientLogoUpload({
  clientId,
  clientName,
  currentUrl,
  updatedAt,
  onUploadComplete,
  onRemoveComplete,
  onError,
}: ClientLogoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleFileSelect = async (file: File) => {
    // Guard against concurrent uploads
    if (isUploading) return;

    // Client-side validation
    if (!ALLOWED_TYPES.includes(file.type)) {
      onError?.("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      onError?.("Image must be less than 2MB");
      return;
    }

    // Revoke previous preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;
    setPreview(previewUrl);
    setIsUploading(true);

    try {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await apiPost(
        `/api/clients/${clientId}/logo/upload-url`,
        {
          contentType: file.type,
          fileSize: file.size,
        }
      );

      if (!uploadUrlResponse.ok) {
        const error = await uploadUrlResponse.json();
        throw new Error(error.detail || "Failed to get upload URL");
      }

      const { data: urlData } = await uploadUrlResponse.json();
      const { uploadUrl, logoUrl } = urlData;

      // Step 2: Upload directly to R2
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image");
      }

      // Step 3: Confirm upload via dedicated logo endpoint
      const confirmResponse = await apiPatch(`/api/clients/${clientId}/logo`, {
        logoUrl,
        updatedAt,
      });

      if (!confirmResponse.ok) {
        const error = await confirmResponse.json();
        throw new Error(error.detail || "Failed to confirm upload");
      }

      const { data: confirmData } = await confirmResponse.json();
      // Clear preview so displayUrl falls through to the new currentUrl from parent
      setPreview(null);
      onUploadComplete(logoUrl, confirmData.updatedAt);
    } catch (error) {
      // Revert preview on error
      setPreview(null);
      onError?.(
        error instanceof Error ? error.message : "Failed to upload logo"
      );
    } finally {
      setIsUploading(false);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    }
  };

  const handleRemove = async () => {
    if (isUploading) return;
    setIsUploading(true);

    try {
      const response = await apiPatch(`/api/clients/${clientId}/logo`, {
        logoUrl: null,
        updatedAt,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to remove logo");
      }

      const { data } = await response.json();
      setPreview(null);
      onRemoveComplete(data.updatedAt);
    } catch (error) {
      onError?.(
        error instanceof Error ? error.message : "Failed to remove logo"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      void handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !isUploading) {
      e.preventDefault();
      handleClick();
    }
  };

  const displayUrl = preview || currentUrl;
  const hasLogo = displayUrl !== null;

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className={cn(
          "relative cursor-pointer group",
          isDragging && "ring-2 ring-primary ring-offset-2 rounded-full",
          isUploading && "pointer-events-none opacity-50"
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={isUploading ? -1 : 0}
        aria-label="Upload client logo"
        aria-disabled={isUploading}
      >
        <ClientAvatar
          name={clientName}
          logoUrl={displayUrl}
          size="lg"
        />

        {/* Upload overlay */}
        {!isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
          </div>
        )}

        {/* Loading spinner */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
            <LoadingSpinner className="h-6 w-6 text-white" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleInputChange}
        className="sr-only"
        aria-label="Choose client logo file"
      />

      <p className="text-sm text-muted-foreground text-center">
        Click or drag to upload a logo
        <br />
        <span className="text-xs">JPEG, PNG, or WebP &mdash; Max 2MB</span>
      </p>

      {hasLogo && !isUploading && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRemove}
          className="text-destructive hover:text-destructive"
        >
          Remove logo
        </Button>
      )}

      {/* Screen reader upload progress */}
      <div aria-live="polite" className="sr-only">
        {isUploading ? "Uploading logo..." : ""}
      </div>
    </div>
  );
}
